import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal, untracked } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { DsyListElement } from '../../../../models/dsy-list-element.model';
import { isSelectedOrganizationFilters, isSelectedProgramFilters } from '../../../../models/filters.model';
import { ProgramOrganizationType } from '../../../../models/program-organization.model';
import { loadOrganizations, loadPrograms } from '../../../../store/actions/pd.actions';
import {
	selectFilters,
	selectOrganizations,
	selectOrganizationsLoading,
	selectProducts,
	selectPrograms,
	selectProgramsLoading,
	selectSites,
	selectViewMode,
} from '../../../../store/selectors/pd.selectors';
import { FiltersStore } from '../../../../store/signal-stores/pd-filters.signal-store';
import { areArraysEqual, sortByLabel } from '../../../shared/helpers/array-helpers';
import { OrganizationFiltersPanelComponent } from './organization-filters-panel/organization-filters-panel.component';
import { ProgramFiltersPanelComponent } from './program-filters-panel/program-filters-panel.component';
import { PaginationStore } from '../../../../store/signal-stores/pagination.signal-store';
import { Router } from '@angular/router';
import { splitIds } from '../../../shared/utils/filter-query-params';

@Component({
  selector: 'fdr-past-performance-ui-past-dispatches-filters',
  imports: [TranslateModule, OrganizationFiltersPanelComponent, ProgramFiltersPanelComponent],
  templateUrl: './past-dispatches-filters.component.html',
  styleUrl: './past-dispatches-filters.component.scss',
  providers: [FiltersStore],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PastDispatchesFiltersComponent {
  // MARK: Injects
  private readonly globalStore = inject(Store);
  readonly localStore = inject(FiltersStore);
  readonly paginationStore = inject(PaginationStore, { optional: true });
  private readonly router = inject(Router);
  // Global selectors as Signals
  readonly programsSignal = this.globalStore.selectSignal(selectPrograms);
  readonly programsLoadingSignal = this.globalStore.selectSignal(selectProgramsLoading);
  readonly organizationsSignal = this.globalStore.selectSignal(selectOrganizations);
  readonly organizationsLoadingSignal = this.globalStore.selectSignal(selectOrganizationsLoading);
  readonly appliedFiltersSignal = this.globalStore.selectSignal(selectFilters);
  readonly sitesSignal = this.globalStore.selectSignal(selectSites);
  readonly productsSignal = this.globalStore.selectSignal(selectProducts);

  // MARK: Variables
  readonly orgDateRangeValid = signal(true);
  readonly progDateRangeValid = signal(true);
  readonly dateResetToken = signal(0);

  // MARK: Computed
  readonly programFilterEntriesSignal = computed<DsyListElement[]>(() => {
    const programs = this.programsSignal();
    const selectedId = this.localStore.program()?.id;
    return (
      programs?.map(p => ({
        id: p.id,
        labelContent: p.name,
        isSelected: p.id === selectedId,
        onListElementClick: () => this.localStore.updateProgram(p),
      })).sort(sortByLabel) || []
    );
  });

  readonly viewMode = this.globalStore.selectSignal(selectViewMode);

  readonly isOrganizationView = computed(() => this.viewMode() === ProgramOrganizationType.Organization);

  readonly productFilterEntriesSignal = computed<DsyListElement[]>(() => {
    const products = this.productsSignal();
    const selectedProducts = this.localStore.products();
    const selectedProductIds = selectedProducts.map(p => p.id);
    return products.map(product => ({
      id: product.id,
      labelContent: product.name,
      isSelected: selectedProductIds.includes(product.id),
      onListElementClick: () => {
        this.localStore.toggleProduct(product);
      },
      hasRightCheckbox: true,
      rightCheckboxSize: 'small',
    })).sort(sortByLabel);
  });


  readonly organizationFilterEntries = computed<DsyListElement[]>(() => {
    const organization = this.organizationsSignal();
    const selectedOrganization = this.localStore.organization();
    const selectedId = selectedOrganization?.id;
    return (
      organization?.map(o => ({
        id: o.id,
        labelContent: o.name,
        isSelected: o.id === selectedId,
        onListElementClick: () => this.localStore.toggleOrganization(o),
        showRightIcon: o.id === selectedId,
        rightIconLibrary: o.id === selectedId ? 'material' : undefined,
        rightIconName: o.id === selectedId ? 'check' : undefined,
        rightIconSize: o.id === selectedId ? 'small' : undefined,
      })).sort(sortByLabel) ?? []
    );
  });

  readonly sitesFilterEntriesSignal = computed<DsyListElement[]>(() => {
    const selectedOrganization = this.localStore.organization();
    if (!selectedOrganization) return [];
    const sites = this.sitesSignal();
    const selectedSiteIds = this.localStore.sites().map(s => s.id);
    return sites.map(site => ({
      id: site.id,
      labelContent: site.name,
      isSelected: selectedSiteIds.includes(site.id),
      onListElementClick: () => {
        this.localStore.toggleSite(site);
      },
      hasRightCheckbox: true,
      rightCheckboxSize: 'small',
    })).sort(sortByLabel);
  });

  readonly showSitesCounterTag = computed(() => this.localStore.sites().length > 1);
  readonly showProductsCounterTag = computed(() => this.localStore.products().length > 1);

  readonly resetButtonDisabledSignal = computed(() => {
    const applied = this.appliedFiltersSignal();
    if (!applied) return true;
    if (isSelectedOrganizationFilters(applied)) {
      const organization = this.localStore.organization();
      const sites = this.localStore.sites();
      if (organization || sites.length) return false;
    }
    if (isSelectedProgramFilters(applied)) {
      const program = this.localStore.program();
      const products = this.localStore.products();
      if (program || products.length) return false;
    }

    return true;
  });

  // Disabled Apply Button Computed Logic
  readonly applyButtonDisabledSignal = computed(() => {
    const loadingPrograms = this.programsLoadingSignal();
    const loadingOrganizations = this.organizationsLoadingSignal();
    if (loadingPrograms || loadingOrganizations) return true;

    const applied = this.appliedFiltersSignal();
    const viewMode = this.viewMode();
    const draft = {
      program: this.localStore.program(),
      products: this.localStore.products(),
      organization: this.localStore.organization(),
      sites: this.localStore.sites(),
    };

    if (viewMode === ProgramOrganizationType.Organization) {
      if (!this.orgDateRangeValid()) return true;
      const dates = this.localStore.orgDateRange();
      if (!draft.organization || !draft.sites.length) return true;
      if (!applied || !isSelectedOrganizationFilters(applied)) return false;
      const isSameOrganization = applied.organization.id === draft.organization.id;
      const isSameDates = applied.dateRange.start.isSame(dates.start, 'day') && applied.dateRange.end.isSame(dates.end, 'day');
      const isSameSites = areArraysEqual(applied.sites || [], draft.sites);
      return isSameOrganization && isSameDates && isSameSites;
    }

    if (!this.progDateRangeValid()) return true;
    const dates = this.localStore.progDateRange();
    if (!draft.program) return true;

    if (!applied || !isSelectedProgramFilters(applied)) return false;
    const isSameProgram = applied.program.id === draft.program.id;
    const isSameDates = applied.dateRange.start.isSame(dates.start, 'day') && applied.dateRange.end.isSame(dates.end, 'day');
    const isSameEntities = areArraysEqual(applied.products || [], draft.products);

    return isSameProgram && isSameDates && isSameEntities;
  });

  // MARK: Constructor and Lifecycle
  constructor() {
    effect(
      () => {
        // Only react to the loading transitions, ignore the rest
        const orgsLoading = this.organizationsLoadingSignal();
        const progsLoading = this.programsLoadingSignal();
        const viewMode = this.viewMode();
        const organizations = untracked(() => this.organizationsSignal());
        const programs = untracked(() => this.programsSignal());
        const applied = untracked(() => this.appliedFiltersSignal());
        if ((!orgsLoading && organizations?.length) || (!progsLoading && programs?.length)) {
          this.localStore.syncFromGlobal(applied, viewMode, programs ?? [], organizations ?? []);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const selectedOrg = this.localStore.organization();
        if (!selectedOrg) return;
        const sites = this.sitesSignal();
        const selectedSites = untracked(() => this.localStore.sites());
        const isFirstLoad = untracked(() => this.localStore.isFirstLoad());
        if (sites.length > 0 && selectedSites.length === 0 && this.viewMode() === ProgramOrganizationType.Organization) {
          // Toggle if first load, otherwise toggle the first site
          // apply the filters if first load, to show something to the user, otherwise just toggle the first site
          if(isFirstLoad) {
            const ids = splitIds(this.router.parseUrl(this.router.url).queryParams['sites']);
            const fromUrl = sites.filter((s) => ids.includes(String(s.id)));
           this.localStore.toggleSites(fromUrl.length ? fromUrl : sites.slice(0, 5));
            this.paginationStore?.resetPageIndex();
            this.onApply();
          } else {
            this.localStore.toggleSite(sites[0]);
          }
        }
        if(isFirstLoad && sites.length > 0) {
          this.localStore.setFirstLoad(false);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const products = this.productsSignal();
        const selectedProducts = untracked(() => this.localStore.products());
        const isFirstLoad = untracked(() => this.localStore.isFirstLoad());
        if (products.length > 0 && selectedProducts.length === 0 && this.viewMode() === ProgramOrganizationType.Program) {
          // Toggle if first load, otherwise toggle the first product
          // apply the filters if first load, to show something to the user, otherwise just toggle the first product
          if(isFirstLoad) {
            const ids = splitIds(this.router.parseUrl(this.router.url).queryParams['products']);
            const fromUrl = products.filter((p) => ids.includes(String(p.id)));
            this.localStore.toggleProducts(fromUrl.length ? fromUrl : products.slice(0, 5));
            this.paginationStore?.resetPageIndex();
            this.onApply();
          } else {
            this.localStore.toggleProduct(products[0]);
          }
        }
        if(isFirstLoad && products.length > 0) {
          this.localStore.setFirstLoad(false);
        }
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      const isOrganizationView = this.isOrganizationView();
      if (isOrganizationView) {
        const orgs = untracked(() => this.organizationsSignal());
        const orgsLoading = untracked(() => this.organizationsLoadingSignal());
        if (!orgs?.length && !orgsLoading) {
          this.globalStore.dispatch(loadOrganizations());
        }
      } else {
        const programs = untracked(() => this.programsSignal());
        const programsLoading = untracked(() => this.programsLoadingSignal());
        if (!programs?.length && !programsLoading) {
          this.globalStore.dispatch(loadPrograms());
        }
      }
    });
  }

  // MARK: Event Handlers
  onApply() {
    if (this.applyButtonDisabledSignal()) return;
    this.paginationStore?.resetPageIndex();
    this.localStore.apply(this.viewMode());
  }

  onReset() {
    this.orgDateRangeValid.set(true);
    this.progDateRangeValid.set(true);
    this.localStore.reset();
    this.dateResetToken.update(v => v + 1);
  }
}
